import { BaseMapInfoWindowBuilder } from "../../../_shared/interfaces/maps/base-map-info-window-builder";
import { getDriverStatusLabelKey } from "./driver-status-label.helper";

export type DashboardMapTranslateFn = (key: string, params?: Record<string, unknown>) => string;

export class DashboardMapInfoWindowBuilder extends BaseMapInfoWindowBuilder {
  /**
   * @param translate Optional translator (this class has no injector). It is called every time an info window is
   * opened, so the labels follow the active language. Without it the labels fall back to English.
   */
  constructor(private readonly translate?: DashboardMapTranslateFn) {
    super();
  }

  private text(key: string, fallback: string, params?: Record<string, unknown>): string {
    return this.escapeHTML(this.translate ? this.translate(key, params) : fallback);
  }

  override buildMapInfoWindowHTML(data: any): string {
    const name = this.escapeHTML(data.driverName);
    const phoneNumber = this.escapeHTML(data.phoneNumber);
    const vehicle = this.escapeHTML(data.vehicle);
    const location = this.escapeHTML(data.location);
    const status = this.escapeHTML(data.status);
    const lat = data.lat;
    const lng = data.lng;

    // Display-only labels. `status` above stays the raw backend value because the icon logic compares against it.
    const rawName = data.driverName ?? "N/A";
    const rawPhoneNumber = data.phoneNumber ?? "N/A";
    const statusLabelKey = getDriverStatusLabelKey(data.status);
    const statusLabel = statusLabelKey ? this.text(statusLabelKey, String(data.status)) : status;
    const callTitle = this.text('driverDashboard.map.callDriver', `Call: ${rawName}`, { name: rawName });
    const telTitle = this.text('driverDashboard.map.telephone', `Tel: ${rawPhoneNumber}`, { phone: rawPhoneNumber });
    const latitudeLabel = this.text('driverDashboard.map.latitude', 'Latitude');
    const longitudeLabel = this.text('driverDashboard.map.longitude', 'Longitude');

    let statusSvg = "";
    if (status === "En Route") {
      statusSvg = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path opacity="0.2" d="M21.75 18H2.25L9 6H15L21.75 18Z" fill="#EE2737" />
          <path
            d="M22.1157 18.6562C22.0296 18.7048 21.9348 18.7358 21.8366 18.7476C21.7385 18.7594 21.639 18.7516 21.5438 18.7248C21.4487 18.6979 21.3598 18.6525 21.2822 18.5912C21.2047 18.5299 21.14 18.4539 21.092 18.3675L14.5595 6.75H12.7482V7.5C12.7482 7.69891 12.6692 7.88968 12.5286 8.03033C12.3879 8.17098 12.1972 8.25 11.9982 8.25C11.7993 8.25 11.6086 8.17098 11.4679 8.03033C11.3273 7.88968 11.2482 7.69891 11.2482 7.5V6.75H9.43699L2.90449 18.3675C2.80702 18.5415 2.64441 18.6697 2.45242 18.7239C2.26043 18.7781 2.05479 18.7537 1.88074 18.6562C1.70669 18.5588 1.57849 18.3962 1.52434 18.2042C1.47019 18.0122 1.49452 17.8065 1.59199 17.6325L7.71574 6.75H2.24824C2.04933 6.75 1.85856 6.67098 1.71791 6.53033C1.57726 6.38968 1.49824 6.19891 1.49824 6C1.49824 5.80109 1.57726 5.61032 1.71791 5.46967C1.85856 5.32902 2.04933 5.25 2.24824 5.25H21.7482C21.9472 5.25 22.1379 5.32902 22.2786 5.46967C22.4192 5.61032 22.4982 5.80109 22.4982 6C22.4982 6.19891 22.4192 6.38968 22.2786 6.53033C22.1379 6.67098 21.9472 6.75 21.7482 6.75H16.2807L22.4045 17.6325C22.453 17.7186 22.4841 17.8135 22.4958 17.9116C22.5076 18.0098 22.4998 18.1093 22.473 18.2044C22.4462 18.2996 22.4008 18.3885 22.3395 18.466C22.2782 18.5435 22.2021 18.6082 22.1157 18.6562ZM11.9982 10.5C11.7993 10.5 11.6086 10.579 11.4679 10.7197C11.3273 10.8603 11.2482 11.0511 11.2482 11.25V12.75C11.2482 12.9489 11.3273 13.1397 11.4679 13.2803C11.6086 13.421 11.7993 13.5 11.9982 13.5C12.1972 13.5 12.3879 13.421 12.5286 13.2803C12.6692 13.1397 12.7482 12.9489 12.7482 12.75V11.25C12.7482 11.0511 12.6692 10.8603 12.5286 10.7197C12.3879 10.579 12.1972 10.5 11.9982 10.5ZM11.9982 15.75C11.7993 15.75 11.6086 15.829 11.4679 15.9697C11.3273 16.1103 11.2482 16.3011 11.2482 16.5V18C11.2482 18.1989 11.3273 18.3897 11.4679 18.5303C11.6086 18.671 11.7993 18.75 11.9982 18.75C12.1972 18.75 12.3879 18.671 12.5286 18.5303C12.6692 18.3897 12.7482 18.1989 12.7482 18V16.5C12.7482 16.3011 12.6692 16.1103 12.5286 15.9697C12.3879 15.829 12.1972 15.75 11.9982 15.75Z"
            fill="#EE2737" 
          />
        </svg>
      `;
    }

    if (status === "Stationary") {
      statusSvg = `
        <svg width="24" height="24" viewBox="0 0 24 24"
             fill="none" xmlns="http://www.w3.org/2000/svg">
          <path opacity="0.2"
            d="M4.5 17.25H8.25V19.5C8.25 19.6989 8.17098 19.8897 8.03033 20.0303C7.88968 20.171 7.69891 20.25 7.5 20.25H5.25C5.05109 20.25 4.86032 20.171 4.71967 20.0303C4.57902 19.8897 4.5 19.6989 4.5 19.5V17.25ZM15.75 19.5C15.75 19.6989 15.829 19.8897 15.9697 20.0303C16.1103 20.171 16.3011 20.25 16.5 20.25H18.75C18.9489 20.25 19.1397 20.171 19.2803 20.0303C19.421 19.8897 19.5 19.6989 19.5 19.5V17.25H15.75V19.5ZM4.5 6.75V10.5H19.5V6.75H4.5Z"
            fill="#EE2737" />
          <path
            d="M17.25 3H6.75C5.95435 3 5.19129 3.31607 4.62868 3.87868C4.06607 4.44129 3.75 5.20435 3.75 6V19.5C3.75 19.8978 3.90804 20.2794 4.18934 20.5607C4.47064 20.842 4.85218 21 5.25 21H7.5C7.89782 21 8.27936 20.842 8.56066 20.5607C8.84196 20.2794 9 19.8978 9 19.5V18H15V19.5C15 19.8978 15.158 20.2794 15.4393 20.5607C15.7206 20.842 16.1022 21 16.5 21H18.75C19.1478 21 19.5294 20.842 19.8107 20.5607C20.092 20.2794 20.25 19.8978 20.25 19.5V6C20.25 5.20435 19.9339 4.44129 19.3713 3.87868C18.8087 3.31607 18.0456 3 17.25 3ZM5.25 16.5V11.25H18.75V16.5H5.25ZM5.25 7.5H18.75V9.75H5.25V7.5ZM6.75 4.5H17.25C17.6478 4.5 18.0294 4.65804 18.3107 4.93934C18.592 5.22064 18.75 5.60218 18.75 6H5.25C5.25 5.60218 5.40804 5.22064 5.68934 4.93934C5.97064 4.65804 6.35218 4.5 6.75 4.5ZM7.5 19.5H5.25V18H7.5V19.5ZM16.5 19.5V18H18.75V19.5H16.5ZM9.75 13.875C9.75 14.0975 9.68402 14.315 9.5604 14.5C9.43679 14.685 9.26109 14.8292 9.05552 14.9144C8.84995 14.9995 8.62375 15.0218 8.40552 14.9784C8.18729 14.935 7.98684 14.8278 7.8295 14.6705C7.67217 14.5132 7.56502 14.3127 7.52162 14.0945C7.47821 13.8762 7.50049 13.65 7.58564 13.4445C7.67078 13.2389 7.81498 13.0632 7.99998 12.9396C8.18499 12.816 8.4025 12.75 8.625 12.75C8.92337 12.75 9.20952 12.8685 9.4205 13.0795C9.63147 13.2905 9.75 13.5766 9.75 13.875ZM16.5 13.875C16.5 14.0975 16.434 14.315 16.3104 14.5C16.1868 14.685 16.0111 14.8292 15.8055 14.9144C15.6 14.9995 15.3738 15.0218 15.1555 14.9784C14.9373 14.935 14.7368 14.8278 14.5795 14.6705C14.4222 14.5132 14.315 14.3127 14.2716 14.0945C14.2282 13.8762 14.2505 13.65 14.3356 13.4445C14.4208 13.2389 14.565 13.0632 14.75 12.9396C14.935 12.816 15.1525 12.75 15.375 12.75C15.6734 12.75 15.9595 12.8685 16.1705 13.0795C16.3815 13.2905 16.5 13.5766 16.5 13.875Z"
            fill="#EE2737" />
        </svg>
      `;
    }

    return `
      <div class="fc-iw">
        <style>
          .gm-style-iw-ch { 
            padding: 0 !important;  
          }

          .gm-style-iw-d { 
            max-height: none !important;
            overflow: visible !important;
            overflow-x: hidden !important; 
          }

          .gm-style-iw, .gm-style-iw-c { 
            overflow: visible !important;
            padding: 16px !important;
            height: auto !important;
            min-width: 200px !important;
            max-width: 267px !important;
          }

          .fc-iw {
            font-family: Arial, sans-serif;
            font-size: 13px;
          }

          /* === TOP: driver + asset + call icon === */
          .fc-iw__header {
            display: flex;
            align-items: flex-start;
            margin-bottom: 10px;
          }

          .fc-iw__title-group {
            flex: 1 1 auto;
            min-width: 0; /* required for flex + ellipsis */
          }

          .fc-iw__driver,
          .fc-iw__asset {
            display: block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .fc-iw__driver {
            font-weight: 600;
            font-size: 14px;
            margin-bottom: 2px;
          }

          .fc-iw__asset {
            font-size: 10px;
            font-weight: 400;
            color: #9FA1A4;
          }

          .fc-iw__call-btn {
            flex: 0 0 auto;
            margin-left: 8px;
            width: 28px;
            height: 28px;
            border-radius: 50%;
            border: none;
            background: #ee2737;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            text-decoration: none;
          }

          .fc-iw__call-btn svg {
            width: 16px;
            height: 16px;
            fill: #ffffff;
          }

          /* === STATUS ROW: icon + text === */
          .fc-iw__status-row {
            display: flex;
            align-items: center;
            margin-bottom: 4px;
          }

          .fc-iw__status-icon {
            flex: 0 0 auto;
            margin-right: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .fc-iw__status-text {
            flex: 1 1 auto;
            min-width: 0;
            font-size: 12px;
            font-weight: 500;
            color: #4f4f4f;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          /* === LOCATION ROW: icon left, text right === */
          .fc-iw__location-row {
            display: flex;
            align-items: center;
            margin-top: 6px;
          }

          .fc-iw__location-icon {
            flex: 0 0 auto;
            margin-right: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .fc-iw__location-icon svg {
            width: 16px;
            height: 16px;
            fill: #ee2737; /* match your design */
          }

          .fc-iw__location-text {
            flex: 1 1 auto;
            min-width: 0;
            font-size: 12px;
            color: #4f4f4f;
            line-height: 1.3;
            white-space: normal;          /* allow wrapping */
            overflow: hidden;
            text-overflow: ellipsis;

            /* multi-line ellipsis (WebKit) */
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;        /* show up to 2 lines, then "…" */
          }

          /* === COORDINATES: label start, value end === */
          .fc-iw__coords {
            margin-top: 6px;
            font-size: 12px;
            display: grid;
            grid-template-columns: auto 1fr; /* label + value */
            column-gap: 8px;
            row-gap: 2px;
            align-items: center;
          }

          .fc-iw__coord-label {
            font-size: 12px;
            color: #9FA1A4;
            text-align: left;
          }

          .fc-iw__coord-value {
            font-size: 12px;
            color: #4f4f4f;
            text-align: right;        /* value to end of container */
            word-break: break-all;
          }

          /* === Existing rows section (we'll refine later) === */
          .fc-iw .rows {
            display: grid;
            grid-template-columns: auto 1fr;
            column-gap: 12px;
            row-gap: 8px;
            align-items: start;
          }

          .fc-iw .label {
            color: #9FA1A4;
            font-weight: 400;
            align-self: start;
          }

          .fc-iw .val {
            align-self: start;
            overflow-wrap: anywhere;
            line-height: 1.25;
            color: black;
          }
        </style>

        <!-- HEADER: driver + asset + call -->
        <div class="fc-iw__header">
          <div class="fc-iw__title-group">
            <span class="fc-iw__driver" title="${name}">${name}</span>
            <span class="fc-iw__asset" title="${vehicle}">${vehicle}</span>
          </div>

          ${
            phoneNumber
              ? `
                <!-- \nClick icon to call on Whatsapp --> <!-- need to get country code for contacts to enable this feature -->
                <a class="fc-iw__call-btn" href="tel:${phoneNumber}" title="${callTitle}\n${telTitle}">
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.56.57 1 1 0 011 1v3.49a1 1 0 01-1 1A17.5 17.5 0 014 5a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.56 1 1 0 01-.25 1.01l-2.2 2.22z"/>
                    </svg>
                </a>
                `
              : ""
          }
        </div>

        <!-- STATUS ROW -->
        ${
          status
            ? `
              <div class="fc-iw__status-row">
                <div class="fc-iw__status-icon">
                  ${statusSvg}
                </div>
                <div class="fc-iw__status-text" title="${statusLabel}">
                  ${statusLabel}
                </div>
              </div>
            `
            : ""
        }

        <!-- LOCATION ROW: icon left, text right -->
        <div class="fc-iw__location-row">
          <div class="fc-iw__location-icon">
            <!-- use the location icon you supplied / want here -->
            <svg style="width: 24; height: 24;" viewBox="0 0 1024 1024" fill="#EE2737" version="1.1" xmlns="http://www.w3.org/2000/svg">
              <path 
                  d="M787.7 339.7C787.7 187.4 664.3 64 512 64c-152.3 0-275.7 123.4-275.7 275.7 0 50.3 13.7 97.2 37.2 137.8h-0.3L512 960l238.9-482.5h-0.3c23.5-40.6 37.1-87.5 37.1-137.8M512 477.5c-76.1 0-137.8-61.7-137.8-137.8 0-76.1 61.7-137.8 137.8-137.8 76.2 0 137.8 61.8 137.8 137.8 0.1 76.1-61.6 137.8-137.8 137.8">
              </path>
            </svg>
          </div>
          <div class="fc-iw__location-text" title="${location}">
            ${location}
          </div>
        </div>

        <!-- COORDINATES -->
        <div class="fc-iw__coords">
          <div class="fc-iw__coord-label">${latitudeLabel}</div>
          <div class="fc-iw__coord-value">${lat}</div>

          <div class="fc-iw__coord-label">${longitudeLabel}</div>
          <div class="fc-iw__coord-value">${lng}</div>
        </div>
      </div>
    `;
  }
}